import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { authApi, mapAuthError } from "@/lib/api/auth";
import { forgotPasswordStore } from "@/lib/forgotPasswordStore";
import { forgotPasswordSchema, type ForgotPasswordValues } from "../schemas/authSchemas";

export function useForgotPasswordForm() {
  const router = useRouter();
  const params = useLocalSearchParams<{ email?: string }>();

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: params.email ?? "" },
  });

  const mutation = useMutation({
    mutationFn: (values: ForgotPasswordValues) => authApi.forgotPassword({ email: values.email }),
    onSuccess: (data, variables) => {
      forgotPasswordStore.setToken(data.otp_token);
      forgotPasswordStore.setCode("");
      router.push({
        pathname: "/(auth)/forgot-password/verify-email" as never,
        params: { email: variables.email },
      });
    },
  });

  const handleSubmit = form.handleSubmit(
    useCallback(
      async (values: ForgotPasswordValues) => {
        await mutation.mutateAsync(values).catch(() => {});
      },
      [mutation.mutateAsync] // eslint-disable-line react-hooks/exhaustive-deps
    )
  );

  const rawError = mutation.error;
  const error = rawError ? mapAuthError(rawError) : null;

  return {
    form,
    isLoading: mutation.isPending,
    error,
    handleSubmit,
    router,
  };
}
